import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  CardTitle, 
  Badge, 
  Table
} from 'react-bootstrap';
import { 
  TextField, 
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import { 
  Visibility as VisibilityIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { imageURL } from '../../imageURL';
import GirviPageShell from '../common/GirviPageShell';
import baseURL from '../../baseURL';
import { computeArticleTypeWeightTotals, formatWeightGrams3 } from '../../utils/pledgeArticleWeights';

const PaidExcessPledges = () => {
  const [pledges, setPledges] = useState([]);
  const [filteredPledges, setFilteredPledges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedPledge, setSelectedPledge] = useState(null);
  const [openDetails, setOpenDetails] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pledgeToDelete, setPledgeToDelete] = useState(null);
  const [masterArticles, setMasterArticles] = useState([]);

  useEffect(() => {
    fetchPledges();
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${baseURL}/articles`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && Array.isArray(data)) setMasterArticles(data);
        else if (Array.isArray(data?.data)) setMasterArticles(data.data);
      } catch (e) {
      }
    };
    fetchArticles();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [dateFrom, dateTo, pledges]);

  const fetchPledges = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/pledges`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch pledges');
      }

      if (data.success) {
        // Filter to only show pledges with paidExcess === true
        const paidExcessPledges = (data.data || []).filter(pledge => 
          pledge.loanDetails?.paidExcess === true
        );
        setPledges(paidExcessPledges);
        setFilteredPledges(paidExcessPledges);
      } else {
        setPledges([]);
        setFilteredPledges([]);
      }
    } catch (error) {
      setPledges([]);
      setFilteredPledges([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (type) => (event) => {
    const date = event.target.value;
    if (type === 'from') {
      setDateFrom(date);
    } else {
      setDateTo(date);
    }
  };

  // Helper function to format date as dd/mm/yyyy
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const applyFilters = () => {
    let filtered = [...pledges];

    // Filter by date range
    if (dateFrom || dateTo) {
      let fromDate = null;
      let toDate = null;
      
      if (dateFrom) {
        fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
      }
      
      if (dateTo) {
        toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
      }
      
      filtered = filtered.filter(pledge => {
        if (!pledge.date) return false;
        const pledgeDate = new Date(pledge.date);
        pledgeDate.setHours(0, 0, 0, 0);
        
        if (fromDate && pledgeDate < fromDate) return false;
        if (toDate && pledgeDate > toDate) return false;
        return true;
      });
    }

    setFilteredPledges(filtered);
  };

  const handleViewDetails = (pledge) => {
    setSelectedPledge(pledge);
    setOpenDetails(true);
  };

  const handleCloseDetails = () => {
    setOpenDetails(false);
  };

  const handleDelete = (pledge) => {
    setPledgeToDelete(pledge);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!pledgeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/pledges/${pledgeToDelete._id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh the pledges list
        fetchPledges();
        setDeleteConfirmOpen(false);
        setPledgeToDelete(null);
      } else {
        alert(data.message || 'Failed to delete pledge');
      }
    } catch (error) {
      alert('Error deleting pledge');
    }
  };

  return (
    <>
      <GirviPageShell
        title="Paid Excess Pledges"
        subtitle="Pledge · List"
        chipLabel="Pledge"
        maxWidth="xl"
        loading={loading}
       compact>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date From"
                type="date"
                value={dateFrom}
                onChange={handleDateChange('from')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date To"
                type="date"
                value={dateTo}
                onChange={handleDateChange('to')}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Container fluid>
          <Row>
            <Col>
              <Table striped bordered hover>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Ticket No</th>
                    <th>Customer Code</th>
                    <th>Customer Name</th>
                    <th>Principal Amount</th>
                    <th>Total Value</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPledges.length > 0 ? (
                    filteredPledges.map(pledge => (
                      <tr key={pledge._id}>
                        <td>{formatDate(pledge.date)}</td>
                        <td>{pledge.loanDetails?.ticketNo || 'N/A'}</td>
                        <td>{pledge.customer?.code || 'N/A'}</td>
                        <td>{`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`.trim() || 'N/A'}</td>
                        <td>₹{(parseFloat(pledge.loanDetails?.principalAmount) || 0).toLocaleString()}</td>
                        <td>₹{(parseFloat(pledge.totalValue) || 0).toLocaleString()}</td>
                        <td>
                          <Badge bg={pledge.status === 'CREATED' || pledge.status === 'ACTIVE' ? 'success' : 'secondary'}>
                            {pledge.status || 'ACTIVE'}
                          </Badge>
                        </td>
                        <td>
                          <IconButton onClick={() => handleViewDetails(pledge)} title="View" color="primary">
                            <VisibilityIcon />
                          </IconButton>
                          <IconButton onClick={() => handleDelete(pledge)} title="Delete" color="error">
                            <DeleteIcon />
                          </IconButton>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className="text-center">No paid excess pledges found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Col>
          </Row>
        </Container>
      </GirviPageShell>

      {/* View Details Dialog */}
      <Dialog open={openDetails} onClose={handleCloseDetails} maxWidth="md" fullWidth>
        <DialogTitle>Pledge Details</DialogTitle>
        <DialogContent>
          {selectedPledge && (
            <Container fluid>
              <Row className="mb-4">
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <CardTitle>Customer Information</CardTitle>
                      <Row>
                        <Col md={6}>
                          <p><strong>Customer Code:</strong> {selectedPledge.customer?.code || 'N/A'}</p>
                          <p><strong>Name:</strong> {selectedPledge.customer?.firstName || 'N/A'} {selectedPledge.customer?.lastName || ''}</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Ticket No:</strong> {selectedPledge.loanDetails?.ticketNo || 'N/A'}</p>
                          <p><strong>Date:</strong> {formatDate(selectedPledge.date)}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <Card.Body>
                      <CardTitle>Loan Information</CardTitle>
                      <Row>
                        <Col md={6}>
                          <p><strong>Principal Amount:</strong> ₹{(parseFloat(selectedPledge.loanDetails?.principalAmount) || 0).toLocaleString()}</p>
                          <p><strong>Duration:</strong> {selectedPledge.loanDetails?.duration || 'N/A'} months</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Rate of Interest:</strong> {selectedPledge.loanDetails?.rateOfInterest || 'N/A'}%</p>
                          <p><strong>Paid Excess:</strong> {selectedPledge.loanDetails?.paidExcess ? 'Yes' : 'No'}</p>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row className="mb-4">
                <Col>
                  <Card>
                    <Card.Body>
                      <CardTitle>Articles</CardTitle>
                      <Table striped bordered hover>
                        <thead>
                          <tr>
                            <th>Description</th>
                            <th>Value</th>
                            <th>Gross Weight</th>
                            <th>Net Weight</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedPledge.articles?.map((article, index) => (
                            <tr key={index}>
                              <td>{article.description || 'N/A'}</td>
                              <td>₹{(parseFloat(article.valueTotal) || 0).toFixed(2)}</td>
                              <td>{formatWeightGrams3(article.grossWeight || article.grossWt)}g</td>
                              <td>{formatWeightGrams3(article.netWeight || article.netWt)}g</td>
                            </tr>
                          )) || (
                            <tr>
                              <td colSpan={4} className="text-center">No articles found</td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                      <div className="text-end mt-3">
                        {(() => {
                          const typeTotals = computeArticleTypeWeightTotals(
                            selectedPledge.articles,
                            masterArticles
                          );
                          if (typeTotals.length >= 2) {
                            return (
                              <>
                                {typeTotals.map((t, idx) => (
                                  <h6 key={idx} className="mb-2">
                                    <strong>{t.title} Gross Weight:</strong> {formatWeightGrams3(t.totalGrossWeight)}g
                                  </h6>
                                ))}
                              </>
                            );
                          }
                          const calculatedTotalGrossWeight = selectedPledge.totalGrossWeight
                            ? parseFloat(selectedPledge.totalGrossWeight)
                            : (selectedPledge.articles || []).reduce((sum, article) => {
                                const weight = parseFloat(article.grossWeight || article.grossWt || 0);
                                return sum + weight;
                              }, 0);

                          const calculatedTotalNetWeight = selectedPledge.totalNetWeight
                            ? parseFloat(selectedPledge.totalNetWeight)
                            : (selectedPledge.articles || []).reduce((sum, article) => {
                                const weight = parseFloat(article.netWeight || article.netWt || 0);
                                return sum + weight;
                              }, 0);

                          return (
                            <>
                              <h6>Total Gross Weight: {formatWeightGrams3(calculatedTotalGrossWeight)}g</h6>
                              <h6>Total Net Weight: {formatWeightGrams3(calculatedTotalNetWeight)}g</h6>
                            </>
                          );
                        })()}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <Row>
                {selectedPledge.fingerprint && (
                  <Col md={6}>
                    <Card>
                      <Card.Body>
                        <CardTitle>Fingerprint</CardTitle>
                        <p>Fingerprint data is stored securely</p>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
                
                {selectedPledge.clientPhoto && (
                  <Col md={6}>
                    <Card>
                      <Card.Img variant="top" src={`${imageURL}/uploads/ledgers/${selectedPledge.clientPhoto}`} alt="Client Photo" className="photo-card" />
                      <Card.Body>
                        <CardTitle>Client Photo</CardTitle>
                      </Card.Body>
                    </Card>
                  </Col>
                )}

                {selectedPledge.articlePhoto && (
                  <Col md={6}>
                    <Card>
                      <Card.Img variant="top" src={`${imageURL}/uploads/ledgers/${selectedPledge.articlePhoto}`} alt="Article Photo" className="photo-card" />
                      <Card.Body>
                        <CardTitle>Article Photo</CardTitle>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>
            </Container>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this pledge? This action cannot be undone.
            {pledgeToDelete && (
              <div style={{ marginTop: '10px' }}>
                <strong>Ticket No:</strong> {pledgeToDelete.loanDetails?.ticketNo || 'N/A'}<br />
                <strong>Customer:</strong> {pledgeToDelete.customer?.firstName || ''} {pledgeToDelete.customer?.lastName || ''}
              </div>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PaidExcessPledges;
